import type {
  Appearance,
  Stripe,
  StripeCheckoutSession,
  StripeCheckoutElementsSdk,
  StripeCheckoutLoadActionsSuccess,
  StripeElementLocale,
  StripePaymentElement,
} from "@stripe/stripe-js";

export const SESSION_ID_QUERY_PARAM = "session_id";

export function useStripe({
  containerRef,
  checkoutSession,
}: {
  containerRef: Ref<HTMLDivElement | undefined>;
  checkoutSession: Ref<CheckoutSessionDto | undefined>;
}) {
  const isLoadingStripe = ref(false);
  const stripe = ref<Stripe>();
  const stripeCheckout = ref<StripeCheckoutElementsSdk>();
  const stripeCheckoutActions = ref<StripeCheckoutLoadActionsSuccess>();
  const stripeElement = ref<StripePaymentElement>();

  const { t, locale } = useI18n();
  const config = useRuntimeConfig();
  const appStore = useAppStore();
  const { handleSafeExecution } = useSafeExecution();

  const isLoading = computed(() => {
    return isLoadingStripe.value || !checkoutSession.value;
  });

  const init = async () => {
    const publicKey: string = (config.public.STRIPE_PUBLIC_KEY as string) || "";
    const { loadStripe } = await import("@stripe/stripe-js");
    const stripeInstance = await loadStripe(publicKey, {
      locale: locale.value as StripeElementLocale, // TODO: use a more robust locale handling (hi does not work)
    });

    if (!stripeInstance) {
      devConsole.error(" 💳 Stripe failed to load. Check your public key.");
      return;
    }

    stripe.value = stripeInstance;
  };

  const initCheckoutForm = async () => {
    if (!stripe.value || !checkoutSession.value) {
      devConsole.error(" 💳 Stripe is not initialized.");
      return;
    }

    try {
      isLoadingStripe.value = true;
      const appearance: Appearance = {
        theme: "stripe",
      };
      stripeCheckout.value = await stripe.value.initCheckoutElementsSdk({
        clientSecret: checkoutSession.value.clientSecret,
        elementsOptions: {
          appearance,
        },
      });
      const actionsResponse = await stripeCheckout.value.loadActions();
      if (actionsResponse.type === "error") {
        devConsole.error(
          " 💳 Error loading Stripe checkout actions:",
          actionsResponse.error,
        );
        return;
      }
      stripeCheckoutActions.value = actionsResponse.actions;

      stripeCheckout.value.on("change", (session: StripeCheckoutSession) => {
        devConsole.info(" 💳 Checkout session changed:", session);
        // Handle changes to the checkout session
      });

      stripeElement.value = stripeCheckout.value.createPaymentElement();
      if (!stripeElement.value) {
        devConsole.error(" 💳 Failed to create Stripe payment element.");
        return;
      }
      stripeElement.value.on("ready", () => {
        devConsole.info(" 💳 Stripe element is ready.");
        isLoadingStripe.value = false;
      });
      stripeElement.value.mount(containerRef.value!);
    } catch (error) {
      devConsole.error(" 💳 Error initializing Stripe Checkout:", error);
      appStore.addError(
        t("cksetup.subscription.payment-step.stripeMountError"),
      );
    } finally {
      isLoadingStripe.value = false;
    }
  };

  const mount = async () => {
    if (!stripe.value) {
      await init();
      if (!stripe.value) {
        devConsole.error(" 💳 Stripe is not initialized.");
        return;
      }
    }

    if (!containerRef.value) {
      devConsole.error(" 💳 Container reference is not set.");
      return;
    }
  };

  const submit = async () => {
    return handleSafeExecution(
      async () => {
        if (
          !stripe.value ||
          !stripeCheckoutActions.value ||
          !checkoutSession.value
        ) {
          devConsole.error("Call mount before submitting.");
          return false;
        }

        const stripeResponse = await stripeCheckoutActions.value.confirm();
        devConsole.info(" 💳 Stripe confirmation response:", stripeResponse);
        if (stripeResponse.type === "error") {
          devConsole.error(
            " 💳 Stripe confirmation error:",
            stripeResponse.error,
          );
          appStore.addError(stripeResponse.error.message);
          return false;
        }

        return true;
      },
      {
        executionName: "Stripe Confirmation",
        showGlobalLoadingIndicator: true,
      },
    );
  };

  watch(
    () => checkoutSession.value && stripe.value,
    (isReady) => {
      if (isReady) {
        initCheckoutForm();
      }
    },
  );

  return {
    isLoading,
    mount,
    submit,
  };
}
